import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

/* ───────────── Animations ───────────── */

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

/* ───────────── Styled Components ───────────── */

const TerminalWrapper = styled(motion.div)`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(13, 17, 23, 0.95);
  border: 1px solid rgba(125, 211, 252, 0.15);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.5),
    0 0 60px rgba(125, 211, 252, 0.05);
  font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'SF Mono', 'Consolas', monospace;
  position: relative;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 8px;
  }
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: rgba(30, 37, 48, 0.9);
  border-bottom: 1px solid rgba(125, 211, 252, 0.1);
  gap: 8px;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
`;

const Dot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ color }) => color};
  opacity: 0.85;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const TitleText = styled.span`
  flex: 1;
  text-align: center;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.5px;
`;

const TerminalBody = styled.div`
  padding: 16px 20px;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 13.5px;
  line-height: 1.7;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(125, 211, 252, 0.3);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    font-size: 11.5px;
    padding: 12px 14px;
    min-height: 180px;
    max-height: 350px;
  }

  @media (max-width: 480px) {
    font-size: 10.5px;
    padding: 10px 10px;
    min-height: 160px;
    max-height: 300px;
  }
`;

const OutputLine = styled.div<{ indent?: boolean }>`
  white-space: pre-wrap;
  word-break: break-word;
  padding-left: ${({ indent }) => (indent ? '16px' : '0')};
`;

const Prompt = styled.span`
  color: #7dd3fc;
  font-weight: 600;
`;

const PromptSeparator = styled.span`
  color: #60a5fa;
`;

const Command = styled.span`
  color: #e2e8f0;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;
`;

const HiddenInput = styled.input`
  position: fixed;
  top: -9999px;
  left: -9999px;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`;

const InputDisplay = styled.span`
  color: #e2e8f0;
  position: relative;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 16px;
  background: #7dd3fc;
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: ${blink} 1s step-end infinite;

  @media (max-width: 480px) {
    height: 13px;
    width: 6px;
  }
`;

const Highlight = styled.span<{ color?: string }>`
  color: ${({ color }) => color || '#7dd3fc'};
`;

const Muted = styled.span`
  color: rgba(255, 255, 255, 0.4);
`;

const Link = styled.a`
  color: #60a5fa;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: #7dd3fc;
  }
`;

const AsciiArt = styled.pre`
  color: #7dd3fc;
  font-size: 10px;
  line-height: 1.15;
  margin: 4px 0;
  opacity: 0.9;
  overflow-x: auto;

  @media (max-width: 768px) {
    font-size: 7px;
  }

  @media (max-width: 480px) {
    font-size: 5.5px;
  }
`;

/* ───────────── Types ───────────── */

interface HistoryEntry {
  type: 'command' | 'output' | 'ascii';
  content: React.ReactNode;
}

/* ───────────── Commands ───────────── */

const ASCII_BANNER = ` █████╗  ██████╗██╗  ██╗██████╗ ███████╗███████╗
██╔══██╗██╔════╝██║  ██║██╔══██╗██╔════╝██╔════╝
███████║██║     ███████║██████╔╝█████╗  █████╗  
██╔══██║██║     ██╔══██║██╔══██╗██╔══╝  ██╔══╝  
██║  ██║╚██████╗██║  ██║██║  ██║███████╗██║     
╚═╝  ╚═╝ ╚═════╝╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝`;

const buildOutput = (cmd: string): HistoryEntry[] => {
  const lower = cmd.trim().toLowerCase();

  switch (lower) {
    case 'help':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Available commands:</Highlight>
              {'\n\n'}
              <Highlight color="#22c55e">  about</Highlight>
              <Muted>{'       — '}</Muted>Who I am{'\n'}
              <Highlight color="#22c55e">  skills</Highlight>
              <Muted>{'      — '}</Muted>My technical stack{'\n'}
              <Highlight color="#22c55e">  experience</Highlight>
              <Muted>{'  — '}</Muted>Work experience{'\n'}
              <Highlight color="#22c55e">  education</Highlight>
              <Muted>{'   — '}</Muted>Academic background{'\n'}
              <Highlight color="#22c55e">  projects</Highlight>
              <Muted>{'    — '}</Muted>Featured projects{'\n'}
              <Highlight color="#22c55e">  contact</Highlight>
              <Muted>{'     — '}</Muted>How to reach me{'\n'}
              <Highlight color="#22c55e">  certs</Highlight>
              <Muted>{'       — '}</Muted>Certifications{'\n'}
              <Highlight color="#22c55e">  clear</Highlight>
              <Muted>{'       — '}</Muted>Clear the terminal
            </>
          ),
        },
      ];

    case 'about':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Achref Samoud</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#f59e0b">  📍</Highlight> Montreal, QC, Canada{'\n'}
              <Highlight color="#f59e0b">  🎓</Highlight> M.Sc.A. Software Engineering @ ÉTS{'\n'}
              <Highlight color="#f59e0b">  💼</Highlight> Research Assistant @ Telus{'\n'}
              <Highlight color="#f59e0b">  🔧</Highlight> DevOps Research • Cloud Infrastructure{'\n'}
              <Highlight color="#f59e0b">  🏆</Highlight> $45K+ Prize Money (International){'\n\n'}
              <Muted>Software engineer focused on DevOps research,</Muted>
              {'\n'}
              <Muted>cloud infrastructure, and developer tooling.</Muted>
            </>
          ),
        },
      ];

    case 'skills':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Technical Skills</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  Languages</Highlight>
              {'    TypeScript • Python • Java • SQL\n'}
              <Highlight color="#22c55e">  Frontend</Highlight>
              {'     React • Next.js • Styled-Components\n'}
              <Highlight color="#22c55e">  Backend</Highlight>
              {'      Node.js • Spring Boot • FastAPI\n'}
              <Highlight color="#22c55e">  DevOps</Highlight>
              {'       Docker • Kubernetes • GitLab CI/CD\n'}
              <Highlight color="#22c55e">  Cloud</Highlight>
              {'        AWS (S3 • CloudFront • EC2)\n'}
              <Highlight color="#22c55e">  Tools</Highlight>
              {'        Git • VS Code • Terraform • Ansible\n'}
              <Highlight color="#22c55e">  AI/ML</Highlight>
              {'        Generative AI • NLP • Robotics'}
            </>
          ),
        },
      ];

    case 'experience':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Work Experience</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  ● </Highlight>
              <Highlight>Research Assistant</Highlight> — Telus
              {'\n'}
              <Muted>    Jan 2025 — Present • Montreal, QC</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  ● </Highlight>
              <Highlight>TA — LOG680 DevOps</Highlight> — ÉTS
              {'\n'}
              <Muted>    May 2025 — Aug 2025 • Montreal, QC</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  ● </Highlight>
              <Highlight>TA — LOG430 Architecture</Highlight> — ÉTS
              {'\n'}
              <Muted>    May 2025 — Present • Montreal, QC</Muted>
              {'\n\n'}
              <Highlight color="#60a5fa">  ● </Highlight>
              <Highlight>Instructor & Tech Lead</Highlight> — Vendogram
              {'\n'}
              <Muted>    Jun 2025 — Oct 2025 • Brossard, QC</Muted>
              {'\n\n'}
              <Highlight color="#a78bfa">  ● </Highlight>
              <Highlight>Mitacs Globalink Intern</Highlight> — ÉTS
              {'\n'}
              <Muted>    May 2024 — Aug 2024 • Montreal, QC</Muted>
              {'\n\n'}
              <Highlight color="#a78bfa">  ● </Highlight>
              <Highlight>Mobile Developer Intern</Highlight> — ComunikCRM
              {'\n'}
              <Muted>    Jul 2023 — Sep 2023 • Ariana, TN</Muted>
              {'\n\n'}
              <Highlight color="#a78bfa">  ● </Highlight>
              <Highlight>Web Developer — MERN Stack</Highlight> — Cognition LLC
              {'\n'}
              <Muted>    Feb 2021 — Jul 2021 • Ariana, TN</Muted>
              {'\n\n'}
              <Muted>  Type 'about' for more details.</Muted>
            </>
          ),
        },
      ];

    case 'education':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Education</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  ◆ </Highlight>
              <Highlight>M.Sc.A. Software Engineering</Highlight>
              {'\n'}
              <Muted>    ÉTS — Montreal • 2025–2027 • GPA 4.06/4.3</Muted>
              {'\n\n'}
              <Highlight color="#60a5fa">  ◆ </Highlight>
              <Highlight>Master in Software Engineering</Highlight>
              {'\n'}
              <Muted>    ISI — Tunisia • 2023–2024 • Honors</Muted>
              {'\n\n'}
              <Highlight color="#60a5fa">  ◆ </Highlight>
              <Highlight>Software Engineering Degree</Highlight>
              {'\n'}
              <Muted>    ISI — Tunisia • 2021–2024 • Honors</Muted>
              {'\n\n'}
              <Highlight color="#60a5fa">  ◆ </Highlight>
              <Highlight>B.Sc. Computer Systems & Software</Highlight>
              {'\n'}
              <Muted>    ISI — Tunisia • 2018–2021 • Honors</Muted>
            </>
          ),
        },
      ];

    case 'projects':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Featured Projects</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#f59e0b">  ▸ </Highlight>
              <Highlight>VS Code Extension</Highlight>
              {' — Architectural constraint detection\n'}
              <Muted>    TypeScript • CI/CD • VS Code API</Muted>
              {'\n\n'}
              <Highlight color="#f59e0b">  ▸ </Highlight>
              <Highlight>Portfolio Website</Highlight>
              {' — This site! React + AWS\n'}
              <Muted>    React • TypeScript • S3 • CloudFront</Muted>
              {'\n\n'}
              <Highlight color="#f59e0b">  ▸ </Highlight>
              <Highlight>DevOps Pipeline Research</Highlight>
              {' — Process improvement\n'}
              <Muted>    GitLab CI/CD • Docker • Kubernetes</Muted>
              {'\n\n'}
              <Highlight color="#f59e0b">  ▸ </Highlight>
              <Highlight>AI Education Platform</Highlight>
              {' — Curriculum & robotics\n'}
              <Muted>    Python • Robotics • Automation</Muted>
            </>
          ),
        },
      ];

    case 'contact':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Contact</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#22c55e">  ✉  </Highlight>
              <Link href="mailto:achref.samoud.1@ens.etsmtl.ca">
                achref.samoud.1@ens.etsmtl.ca
              </Link>
              {'\n'}
              <Highlight color="#22c55e">  💼 </Highlight>
              <Link href="https://www.linkedin.com/in/achref-samoud/" target="_blank" rel="noopener noreferrer">
                linkedin.com/in/achref-samoud
              </Link>
              {'\n'}
              <Highlight color="#22c55e">  🐙 </Highlight>
              <Link href="https://github.com/AchrefSamoud" target="_blank" rel="noopener noreferrer">
                github.com/AchrefSamoud
              </Link>
              {'\n'}
              <Highlight color="#22c55e">  🦊 </Highlight>
              <Link href="https://gitlab.com/achrefsam" target="_blank" rel="noopener noreferrer">
                gitlab.com/achrefsam
              </Link>
            </>
          ),
        },
      ];

    case 'certs':
    case 'certifications':
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight>Certifications</Highlight>
              {'\n'}
              <Muted>━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</Muted>
              {'\n\n'}
              <Highlight color="#0078d4">  Microsoft</Highlight>
              {'  Azure Fundamentals • Azure Data\n'}
              <Highlight color="#ff9900">  AWS</Highlight>
              {'        Generative AI • Cloud Essentials\n'}
              <Highlight color="#326CE5">  K8s</Highlight>
              {'        Kubernetes • Docker\n'}
              <Highlight color="#EE0000">  IBM</Highlight>
              {'        DevOps, Cloud & Agile\n'}
              <Highlight color="#7B42BC">  Ansible</Highlight>
              {'    Automation Basics\n\n'}
              <Muted>  Scroll down to see full details ↓</Muted>
            </>
          ),
        },
      ];

    case 'clear':
      return [];

    case '':
      return [];

    default:
      return [
        {
          type: 'output',
          content: (
            <>
              <Highlight color="#ef4444">  command not found: </Highlight>
              {cmd}
              {'\n'}
              <Muted>  Type 'help' to see available commands.</Muted>
            </>
          ),
        },
      ];
  }
};

/* ───────────── Welcome Message ───────────── */

const welcomeEntries: HistoryEntry[] = [
  {
    type: 'ascii',
    content: ASCII_BANNER,
  },
  {
    type: 'output',
    content: (
      <>
        {'\n'}
        <Muted>  Welcome to my interactive portfolio!</Muted>
        {'\n'}
        <Muted>  Type </Muted>
        <Highlight color="#22c55e">'help'</Highlight>
        <Muted> to list available commands.</Muted>
      </>
    ),
  },
];

/* ───────────── Component ───────────── */

const Terminal: React.FC = () => {
  const [history, setHistory] = useState<HistoryEntry[]>(welcomeEntries);
  const [input, setInput] = useState('');
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // auto-scroll
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [history, input]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = input.trim();

    const commandEntry: HistoryEntry = {
      type: 'command',
      content: trimmed,
    };

    if (trimmed.toLowerCase() === 'clear') {
      setHistory(welcomeEntries);
      setInput('');
      if (trimmed) {
        setCommandHistory((prev) => [trimmed, ...prev]);
      }
      setHistoryIdx(-1);
      return;
    }

    const output = buildOutput(trimmed);

    setHistory((prev) => [
      ...prev,
      ...(trimmed ? [commandEntry] : [commandEntry]),
      ...output,
    ]);

    if (trimmed) {
      setCommandHistory((prev) => [trimmed, ...prev]);
    }
    setInput('');
    setHistoryIdx(-1);
  }, [input]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (commandHistory.length > 0) {
          const next = Math.min(historyIdx + 1, commandHistory.length - 1);
          setHistoryIdx(next);
          setInput(commandHistory[next]);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (historyIdx > 0) {
          const next = historyIdx - 1;
          setHistoryIdx(next);
          setInput(commandHistory[next]);
        } else {
          setHistoryIdx(-1);
          setInput('');
        }
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const cmds = ['help', 'about', 'skills', 'experience', 'education', 'projects', 'contact', 'certs', 'clear'];
        const match = cmds.filter((c) => c.startsWith(input.toLowerCase()));
        if (match.length === 1) {
          setInput(match[0]);
        }
      }
    },
    [handleSubmit, commandHistory, historyIdx, input]
  );

  return (
    <TerminalWrapper
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.5 }}
    >
      {/* Title bar */}
      <TitleBar>
        <Dots>
          <Dot color="#ff5f57" />
          <Dot color="#febc2e" />
          <Dot color="#28c840" />
        </Dots>
        <TitleText>achref@portfolio — zsh</TitleText>
        <div style={{ width: 50 }} />
      </TitleBar>

      {/* Hidden input outside scroll area */}
      <HiddenInput
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
      />

      {/* Body */}
      <TerminalBody ref={bodyRef} onClick={focusInput}>
        {history.map((entry, i) => {
          if (entry.type === 'ascii') {
            return <AsciiArt key={i}>{entry.content}</AsciiArt>;
          }
          if (entry.type === 'command') {
            return (
              <OutputLine key={i}>
                <Prompt>achref</Prompt>
                <PromptSeparator>@portfolio</PromptSeparator>
                <Muted>:~$ </Muted>
                <Command>{entry.content}</Command>
              </OutputLine>
            );
          }
          return <OutputLine key={i}>{entry.content}</OutputLine>;
        })}

        {/* Active prompt */}
        <InputRow>
          <Prompt>achref</Prompt>
          <PromptSeparator>@portfolio</PromptSeparator>
          <Muted>:~$ </Muted>
          <InputDisplay>{input}</InputDisplay>
          <Cursor />
        </InputRow>
      </TerminalBody>
    </TerminalWrapper>
  );
};

export default Terminal;
